import { ApplyReasonCode, PresentationModification, TableData } from "../types";
import { buildTableTextUpdatesFromRows, mergeTableTextUpdates } from "./tableData";

type TableAction = NonNullable<TableData["action"]>;
type TableStructureOp = NonNullable<TableData["structureOps"]>[number];

const TABLE_ACTIONS: ReadonlySet<TableAction> = new Set<TableAction>([
  "create",
  "edit",
  "replace",
  "relayout",
]);

export interface TableOpsNormalizationIssue {
  code: ApplyReasonCode;
  message: string;
}

export interface TableOpsNormalizationStats {
  touchedShapes: number;
  normalizedOps: number;
  droppedOps: number;
  convertedLegacyCellUpdates: number;
  issues: TableOpsNormalizationIssue[];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function toFiniteNumber(value: unknown): number | undefined {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string") {
    const parsed = Number.parseFloat(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return undefined;
}

function normalizeNonNegativeInt(value: unknown): number | undefined {
  const asNumber = toFiniteNumber(value);
  if (asNumber === undefined) {
    return undefined;
  }
  const intVal = Math.floor(asNumber);
  return intVal >= 0 ? intVal : undefined;
}

function normalizePositiveInt(value: unknown): number | undefined {
  const asNumber = normalizeNonNegativeInt(value);
  return asNumber && asNumber > 0 ? asNumber : undefined;
}

function normalizeRows(rows: unknown): string[][] | undefined {
  if (!Array.isArray(rows) || rows.length === 0) {
    return undefined;
  }

  const normalizedRows = rows
    .filter((row) => Array.isArray(row))
    .map((row) =>
      row.map((cell) =>
        typeof cell === "string" ? cell : cell === null || cell === undefined ? "" : String(cell)
      )
    );
  if (normalizedRows.length === 0) {
    return undefined;
  }

  const maxColumns = Math.max(...normalizedRows.map((row) => row.length), 0);
  if (maxColumns <= 0) {
    return undefined;
  }

  return normalizedRows.map((row) =>
    row.length < maxColumns ? [...row, ...Array.from({ length: maxColumns - row.length }, () => "")] : row
  );
}

function normalizeTextUpdates(updates: unknown): NonNullable<TableData["textUpdates"]> {
  if (!Array.isArray(updates)) {
    return [];
  }
  return updates
    .map((entry) => {
      if (!isRecord(entry)) {
        return null;
      }
      const row = normalizeNonNegativeInt(entry.row);
      const col = normalizeNonNegativeInt(entry.col);
      if (row === undefined || col === undefined) {
        return null;
      }
      const text =
        typeof entry.text === "string"
          ? entry.text
          : entry.text === null || entry.text === undefined
            ? ""
            : String(entry.text);
      return { row, col, text };
    })
    .filter((entry): entry is NonNullable<typeof entry> => entry !== null);
}

function normalizeStyleUpdates(updates: unknown): NonNullable<TableData["styleUpdates"]> {
  if (!Array.isArray(updates)) {
    return [];
  }
  return updates
    .map((entry) => {
      if (!isRecord(entry) || !isRecord(entry.target) || !isRecord(entry.set)) {
        return null;
      }
      const targetType = entry.target.type;
      if (typeof targetType !== "string") {
        return null;
      }
      if (targetType === "all") {
        return { target: { type: "all" } as const, set: entry.set };
      }
      if (targetType === "row" || targetType === "column") {
        const index = normalizeNonNegativeInt(entry.target.index);
        if (index === undefined) {
          return null;
        }
        return { target: { type: targetType, index } as const, set: entry.set };
      }
      if (targetType === "cell") {
        const row = normalizeNonNegativeInt(entry.target.row);
        const col = normalizeNonNegativeInt(entry.target.col);
        if (row === undefined || col === undefined) {
          return null;
        }
        return { target: { type: "cell", row, col } as const, set: entry.set };
      }
      if (targetType === "range") {
        const startRow = normalizeNonNegativeInt(entry.target.startRow);
        const startCol = normalizeNonNegativeInt(entry.target.startCol);
        const rowCount = normalizePositiveInt(entry.target.rowCount);
        const colCount = normalizePositiveInt(entry.target.colCount);
        if (
          startRow === undefined ||
          startCol === undefined ||
          rowCount === undefined ||
          colCount === undefined
        ) {
          return null;
        }
        return {
          target: { type: "range", startRow, startCol, rowCount, colCount } as const,
          set: entry.set,
        };
      }
      return null;
    })
    .filter((entry): entry is NonNullable<typeof entry> => entry !== null);
}

function normalizeStructureOps(ops: unknown): NonNullable<TableData["structureOps"]> {
  if (!Array.isArray(ops)) {
    return [];
  }
  return ops
    .map((entry): TableStructureOp | null => {
      if (!isRecord(entry) || typeof entry.op !== "string") {
        return null;
      }
      switch (entry.op) {
        case "addRow":
        case "deleteRow":
        case "addColumn":
        case "deleteColumn": {
          const op = entry.op;
          const index = normalizeNonNegativeInt(entry.index);
          if (index === undefined) {
            return null;
          }
          const count = normalizePositiveInt(entry.count);
          return count ? { op, index, count } : { op, index };
        }
        case "mergeCells":
        case "splitCell":
        case "resizeCellSpan": {
          const op = entry.op;
          const rowIndex = normalizeNonNegativeInt(entry.rowIndex);
          const columnIndex = normalizeNonNegativeInt(entry.columnIndex);
          const rowCount = normalizePositiveInt(entry.rowCount);
          const columnCount = normalizePositiveInt(entry.columnCount);
          if (
            rowIndex === undefined ||
            columnIndex === undefined ||
            rowCount === undefined ||
            columnCount === undefined
          ) {
            return null;
          }
          return {
            op,
            rowIndex,
            columnIndex,
            rowCount,
            columnCount,
          };
        }
        default:
          return null;
      }
    })
    .filter((entry): entry is TableStructureOp => entry !== null);
}

function normalizeClearOps(clearOps: unknown): NonNullable<TableData["clearOps"]> {
  if (!Array.isArray(clearOps)) {
    return [];
  }
  return clearOps
    .map((entry) => {
      if (!isRecord(entry) || typeof entry.mode !== "string") {
        return null;
      }
      if (!["all", "text_only", "format_only"].includes(entry.mode)) {
        return null;
      }
      return { mode: entry.mode as "all" | "text_only" | "format_only" };
    })
    .filter((entry): entry is NonNullable<typeof entry> => entry !== null);
}

function inferAction(shapeAction: string, table: TableData): TableAction {
  if (shapeAction === "modify") {
    const hasEditIntent = Boolean(
      (table.rows && table.rows.length > 0) ||
      (table.textUpdates && table.textUpdates.length > 0) ||
        (table.styleUpdates && table.styleUpdates.length > 0) ||
        (table.structureOps && table.structureOps.length > 0) ||
        (table.clearOps && table.clearOps.length > 0) ||
        table.styleSettingsUpdate
    );
    if (table.layoutSpec && !hasEditIntent) {
      return "relayout";
    }
    return "edit";
  }
  return "create";
}

export function normalizeTableOpsInModifications(
  modifications: PresentationModification
): TableOpsNormalizationStats {
  const stats: TableOpsNormalizationStats = {
    touchedShapes: 0,
    normalizedOps: 0,
    droppedOps: 0,
    convertedLegacyCellUpdates: 0,
    issues: [],
  };

  for (const slide of modifications.slides || []) {
    for (const shape of slide.shapes || []) {
      let touchedShape = false;

      const legacyTextUpdates = normalizeTextUpdates(shape.cellUpdates);
      let consumedLegacyTextUpdates = false;
      if (legacyTextUpdates.length > 0 && !shape.table) {
        shape.table = { action: "edit", textUpdates: legacyTextUpdates };
        delete shape.cellUpdates;
        stats.convertedLegacyCellUpdates += 1;
        stats.normalizedOps += legacyTextUpdates.length;
        touchedShape = true;
        consumedLegacyTextUpdates = true;
      }

      if (!shape.table) {
        if (touchedShape) {
          stats.touchedShapes += 1;
        }
        continue;
      }

      if (legacyTextUpdates.length > 0 && !consumedLegacyTextUpdates) {
        const mergedTextUpdates = [
          ...(shape.table.textUpdates || []),
          ...legacyTextUpdates,
        ];
        shape.table.textUpdates = normalizeTextUpdates(mergedTextUpdates);
        delete shape.cellUpdates;
        touchedShape = true;
      }

      if (shape.table.action && !TABLE_ACTIONS.has(shape.table.action)) {
        stats.droppedOps += 1;
        stats.issues.push({
          code: "MODEL_OUTPUT_INVALID_TABLE_OP",
          message: `Dropped invalid table action "${String(shape.table.action)}" on shape ${shape.id}.`,
        });
        delete shape.table;
        touchedShape = true;
        if (touchedShape) {
          stats.touchedShapes += 1;
        }
        continue;
      }

      const normalizedRows = normalizeRows(shape.table.rows);
      if (shape.table.rows && !normalizedRows) {
        stats.droppedOps += 1;
        stats.issues.push({
          code: "MODEL_OUTPUT_INVALID_TABLE_OP",
          message: `Dropped invalid table.rows payload on shape ${shape.id}.`,
        });
        delete shape.table.rows;
      } else if (normalizedRows) {
        shape.table.rows = normalizedRows;
        touchedShape = true;
      }

      const normalizedTextUpdates = normalizeTextUpdates(shape.table.textUpdates);
      if ((shape.table.textUpdates || []).length !== normalizedTextUpdates.length) {
        stats.droppedOps += (shape.table.textUpdates || []).length - normalizedTextUpdates.length;
        touchedShape = true;
      }
      shape.table.textUpdates = normalizedTextUpdates.length > 0 ? normalizedTextUpdates : undefined;

      const inferredAction = inferAction(shape.action, shape.table);
      shape.table.action = shape.table.action || inferredAction;

      const normalizedStyleUpdates = normalizeStyleUpdates(shape.table.styleUpdates);
      if ((shape.table.styleUpdates || []).length !== normalizedStyleUpdates.length) {
        stats.droppedOps += (shape.table.styleUpdates || []).length - normalizedStyleUpdates.length;
        touchedShape = true;
      }
      shape.table.styleUpdates = normalizedStyleUpdates.length > 0 ? normalizedStyleUpdates : undefined;

      const normalizedStructureOps = normalizeStructureOps(shape.table.structureOps);
      if ((shape.table.structureOps || []).length !== normalizedStructureOps.length) {
        stats.droppedOps += (shape.table.structureOps || []).length - normalizedStructureOps.length;
        touchedShape = true;
      }
      shape.table.structureOps = normalizedStructureOps.length > 0 ? normalizedStructureOps : undefined;

      const normalizedClearOps = normalizeClearOps(shape.table.clearOps);
      if ((shape.table.clearOps || []).length !== normalizedClearOps.length) {
        stats.droppedOps += (shape.table.clearOps || []).length - normalizedClearOps.length;
        touchedShape = true;
      }
      shape.table.clearOps = normalizedClearOps.length > 0 ? normalizedClearOps : undefined;

      const requiresRows = shape.table.action === "create" || shape.table.action === "replace";
      if (requiresRows && (!shape.table.rows || shape.table.rows.length === 0)) {
        stats.droppedOps += 1;
        stats.issues.push({
          code: "MODEL_OUTPUT_INVALID_TABLE_OP",
          message: `Dropped table operation on shape ${shape.id}; action "${shape.table.action}" requires rows.`,
        });
        delete shape.table;
        touchedShape = true;
        if (touchedShape) {
          stats.touchedShapes += 1;
        }
        continue;
      }

      if (requiresRows && shape.table.rows) {
        const derivedTextUpdates = buildTableTextUpdatesFromRows(shape.table.rows);
        const mergedTextUpdates = mergeTableTextUpdates(derivedTextUpdates, normalizedTextUpdates);
        if (
          mergedTextUpdates.length !== normalizedTextUpdates.length ||
          mergedTextUpdates.some((update, index) => {
            const prior = normalizedTextUpdates[index];
            return !prior || prior.row !== update.row || prior.col !== update.col || prior.text !== update.text;
          })
        ) {
          stats.normalizedOps += Math.max(0, mergedTextUpdates.length - normalizedTextUpdates.length);
          touchedShape = true;
        }
        shape.table.textUpdates = mergedTextUpdates.length > 0 ? mergedTextUpdates : undefined;
      } else {
        shape.table.textUpdates = normalizedTextUpdates.length > 0 ? normalizedTextUpdates : undefined;
      }

      const hasPayload =
        (shape.table.rows && shape.table.rows.length > 0) ||
        (shape.table.textUpdates && shape.table.textUpdates.length > 0) ||
        (shape.table.styleUpdates && shape.table.styleUpdates.length > 0) ||
        (shape.table.structureOps && shape.table.structureOps.length > 0) ||
        (shape.table.clearOps && shape.table.clearOps.length > 0) ||
        Boolean(shape.table.layoutSpec) ||
        Boolean(shape.table.styleSettingsUpdate) ||
        Boolean(shape.table.styleSettings) ||
        Boolean(shape.table.uniformCellProperties) ||
        Boolean(shape.table.specificCellProperties && shape.table.specificCellProperties.length > 0);

      if (!hasPayload) {
        stats.droppedOps += 1;
        stats.issues.push({
          code: "MODEL_OUTPUT_INVALID_TABLE_OP",
          message: `Dropped empty table operation payload on shape ${shape.id}.`,
        });
        delete shape.table;
        touchedShape = true;
      } else {
        touchedShape = true;
      }

      if (touchedShape) {
        stats.touchedShapes += 1;
      }
    }
  }

  if (stats.droppedOps > 0 && stats.issues.length === 0) {
    stats.issues.push({
      code: "MODEL_OUTPUT_INVALID_TABLE_OP",
      message: `Dropped ${stats.droppedOps} invalid table operation payload item(s).`,
    });
  }

  return stats;
}
